import { ConfiguratorScene } from '@3d-scene/scene/configurator-scene';
import { Euler, OrthographicCamera, Quaternion, Vector3 } from 'three';
import { GlobalContainer, PositionAndRotation } from '@3d-scene/model3d/global-container';
import { CameraParametersVO } from '@3d-scene/camera-parameters.vo';
import { getMouseNavigationParameters } from '@3d-scene/mousenavigatorparameters/mouse-navigation-parameters';
import { ProjectModel } from '@project/model/project.model';
import { Injectable } from '@angular/core';
import { GlobalArticleContainerUtils } from '../model3d/util/global-article-container-utils';
import { ConfiguratorParameters } from '../configurator-parameters/configurator-parameters';
import { CameraControl } from '../common/scene/controls/camera-control';
import { DefaultCameraPositionParams } from '../common/scene/controls/camera-controls.interface';
import { MathUtils } from '../common/util/math-utils';

@Injectable({ providedIn: 'root' })
export class SceneManager {
  private readonly DRILLING_HOLES_VIEW_DEFAULT_OFFSET_BY_X: number = 400;
  private readonly DRILLING_HOLES_VIEW_DEFAULT_OFFSET_BY_Y: number = 200;

  constructor(
    private globalContainer: GlobalContainer,
    private projectModel: ProjectModel,
    private configuratorScene: ConfiguratorScene,
    private globalArticleContainerUtils: GlobalArticleContainerUtils,
  ) {
    ConfiguratorParameters.maxRendererAnisotropy = this.configuratorScene.rendererCapabilities.getMaxAnisotropy();
  }

  public get sceneControls(): CameraControl {
    return this.configuratorScene.controls;
  }

  public get currentPositionAndRotation(): PositionAndRotation {
    return {
      position: this.globalContainer.position.clone(),
      rotation: this.globalContainer.rotation.clone()
    };
  }

  public createScene(canvas: HTMLCanvasElement): void {
    this.configuratorScene.create(canvas);
    this.configuratorScene.addObjectToScene(this.globalContainer);
  }

  public renderScene(): void {
    this.configuratorScene?.renderScene();
  }

  public moveCoordinateCenter(newCoordinateCenter: Vector3, newCoordinateRotation: Euler): void {
    this.globalContainer.rotateByEuler(newCoordinateRotation);
    this.globalContainer.moveBy(newCoordinateCenter.x, newCoordinateCenter.y, newCoordinateCenter.z);
    this.renderScene();
  }

  public reverseMoveCoordinateCenter(newCoordinateCenter: Vector3, newCoordinateRotation: Euler): void {
    this.globalContainer.moveBy(-newCoordinateCenter.x, -newCoordinateCenter.y, -newCoordinateCenter.z);
    this.globalContainer.inverseRotateByEuler(newCoordinateRotation);
    this.renderScene();
  }

  public createCurrentCameraParameters(): CameraParametersVO | undefined {
    if (!this.configuratorScene) {
      return;
    }
    const camera: OrthographicCamera = this.configuratorScene.camera;
    const parameters: CameraParametersVO = new CameraParametersVO();
    const position: Vector3 = camera.position.clone();
    const direction = new Vector3();
    camera.getWorldDirection(direction);

    if (direction) {
      parameters.lookAtPositionX = position.x + direction.x;
      parameters.lookAtPositionY = position.y + direction.y;
      parameters.lookAtPositionZ = position.z + direction.z;
    }

    if (position) {
      parameters.cameraPositionX = position.x;
      parameters.cameraPositionY = position.y;
      parameters.cameraPositionZ = position.z;
    }
    const upVector: Vector3 = camera.up.clone();
    if (upVector) {
      parameters.upVectorX = upVector.x;
      parameters.upVectorY = upVector.y;
      parameters.upVectorZ = upVector.z;
    }

    let defaultDistance: number = 1;

    defaultDistance = getMouseNavigationParameters(this.projectModel.currentProjectType).defaultDistancePdfCameraParameter;
    parameters.viewHeight = (Math.abs(camera.top) + Math.abs(camera.bottom)) / camera.zoom;
    parameters.viewWidth = (Math.abs(camera.right) + Math.abs(camera.left)) / camera.zoom;
    parameters.scale = defaultDistance / parameters.viewHeight;

    return parameters;
  }

  public createDefaultIsometricCameraParameters(): CameraParametersVO | undefined {
    if (!this.configuratorScene) {
      return;
    }
    const controls = this.configuratorScene.controls;
    const camera: OrthographicCamera = this.configuratorScene.camera;

    // save current camera settings to variables
    const cameraClone: OrthographicCamera = camera.clone(true) as OrthographicCamera;

    // apply default position and isometric rotation to camera to retrieve parameters
    // set camera lens value as in flex (change left, right, top, bottom and set zoom to 1)
    const params: DefaultCameraPositionParams = getMouseNavigationParameters(this.projectModel.currentProjectType);
    if (params.isViewAllInDefaultPositionRequired) {
      params.bbox = this.globalArticleContainerUtils.calculateBBoxForAllArticlesInGlobal();
    }
    params.defaultViewMarginLeft = ConfiguratorParameters.view.defaultViewMarginLeft;
    params.canvasWidth = -this.configuratorScene.canvasWidth;
    params.pdfMode = true;
    controls.setCameraDefaultPosition(params, false);
    controls.view('isometric');

    const parameters = this.createCurrentCameraParameters();

    // restore currently used camera settings
    controls.setCameraFromClone(cameraClone);
    return parameters;
  }

  public setFocusAtCanvas(): void {
    this.configuratorScene?.setFocusAtCanvas();
  }

  /**
   * Toggle for "Drilled hole view with dimensions"
   * @param isDrillingHolesView if true: moves the camera to the left if the default camera position is active --
   * if false: moves the camera to the right if the default camera position+offset is active
   */
  public checkAndMoveCameraToDefaultPositionBeforeDrillingHolesView(isDrillingHolesView: boolean): void {
    let isDefaultLookPosition: boolean = false;
    let moveXOffset: number;
    let moveYOffset: number;

    const defaultPosition = new Vector3();
    const defaultQuaternion = new Quaternion();
    const defaultScale = new Vector3();
    this.configuratorScene.controls.defaultCameraMatrix.decompose(defaultPosition, defaultQuaternion, defaultScale);

    const currentPosition = new Vector3();
    const currentQuaternion = new Quaternion();
    const currentScale = new Vector3();
    this.configuratorScene.controls.currentCameraMatrix.decompose(currentPosition, currentQuaternion, currentScale);

    if (isDrillingHolesView) {
      isDefaultLookPosition = MathUtils.equalsNumber3D(currentPosition, defaultPosition);
      moveXOffset = this.DRILLING_HOLES_VIEW_DEFAULT_OFFSET_BY_X;
      moveYOffset = this.DRILLING_HOLES_VIEW_DEFAULT_OFFSET_BY_Y;
    } else {
      defaultPosition.x += this.DRILLING_HOLES_VIEW_DEFAULT_OFFSET_BY_X;
      defaultPosition.z += this.DRILLING_HOLES_VIEW_DEFAULT_OFFSET_BY_Y;

      isDefaultLookPosition = MathUtils.equalsNumber3D(currentPosition, defaultPosition);
      moveXOffset = -this.DRILLING_HOLES_VIEW_DEFAULT_OFFSET_BY_X;
      moveYOffset = -this.DRILLING_HOLES_VIEW_DEFAULT_OFFSET_BY_Y;
    }

    if (
      defaultScale.equals(currentScale)
      && defaultQuaternion.equals(currentQuaternion)
      && isDefaultLookPosition
    ) {
      this.configuratorScene.controls.move(moveXOffset, moveYOffset);
    }
  }
}
